import React, { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";
import styled from "styled-components";

// --- Styled Components ---
const ChartWrapper = styled.div`
  background-color: #f9f9f9;
  width: 100%;
  height: 60vh;
  padding: 20px;
  position: relative;
`;

const TitleWrapper = styled.div`
  margin-bottom: 12px;
`;

const ChartTitle = styled.h3`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  text-align: center;
  color: #666;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #000;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  align-items: center;
  margin-bottom: 10px;

  select {
    padding: 6px 10px;
    font-size: 14px;
    border: 1px solid #ccc;
    border-radius: 4px;
  }

  label {
    font-size: 14px;
    color: #333;
  }
`;

const monthNamesPL = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
];

// --- Component ---
const NewVsRegularSuppliers = ({ data }) => {
  const [selectedYear, setSelectedYear] = useState(null);

  const availableYears = useMemo(() => {
    const years = new Set();
    data.forEach(item => {
      const rawDate = item.DataZakupu;
      const date = new Date(rawDate);
      if (!isNaN(date)) {
        years.add(date.getFullYear());
      }
    });
    return Array.from(years).sort();
  }, [data]);

  const monthlyStats = useMemo(() => {
    if (!data || data.length === 0) return [];

    const supplierFirstPurchase = {};
    const monthlySupplierMap = {};

    data.forEach((item) => {
      const supplierId = item.NrDostawcy;
      const rawDate = item.DataZakupu;
      const date = new Date(rawDate);

      if (!supplierId || isNaN(date)) return;

      const year = date.getFullYear();
      const month = date.getMonth(); // 0-indexed
      const monthKey = `${year}-${String(month + 1).padStart(2, "0")}`;

      if (!supplierFirstPurchase[supplierId]) {
        supplierFirstPurchase[supplierId] = date;
      }

      if (!monthlySupplierMap[monthKey]) {
        monthlySupplierMap[monthKey] = new Set();
      }

      monthlySupplierMap[monthKey].add(supplierId);
    });

    const result = Object.keys(monthlySupplierMap)
      .sort()
      .map((monthKey) => {
        const [yearStr, monthStr] = monthKey.split("-");
        const year = parseInt(yearStr);
        const monthIndex = parseInt(monthStr, 10) - 1;

        if (selectedYear && year !== selectedYear) return null;

        const suppliers = monthlySupplierMap[monthKey];
        let newCount = 0;
        let repeatCount = 0;

        suppliers.forEach((sid) => {
          const firstPurchase = supplierFirstPurchase[sid];
          const firstKey = `${firstPurchase.getFullYear()}-${String(
            firstPurchase.getMonth() + 1
          ).padStart(2, "0")}`;
          if (firstKey === monthKey) {
            newCount++;
          } else {
            repeatCount++;
          }
        });

        return {
          month: monthNamesPL[monthIndex],
          Nowi: newCount,
          Stali: repeatCount,
        };
      })
      .filter(Boolean); // remove nulls

    return result;
  }, [data, selectedYear]);

  return (
    <ChartWrapper>
      <InfoIconWrapper data-tooltip-id="new-regular-suppliers">
        <Info />
      </InfoIconWrapper>
      <InfoTooltip
        id="new-regular-suppliers"
        place="left"
        content={
          <>
            <strong>Nowi vs. Stali Dostawcy</strong>
            <br />
            Liczba dostawców, którzy dokonali pierwszej dostawy (Nowi)
            <br />
            i tych, którzy dostarczali już wcześniej (Stali).
          </>
        }
      />

      {availableYears.length > 0 && (
        <FiltersWrapper>
          <div>
            <label htmlFor="yearSelect">Rok:</label>
            <select
              id="yearSelect"
              value={selectedYear || ""}
              onChange={(e) =>
                setSelectedYear(e.target.value ? parseInt(e.target.value) : null)
              }
            >
              <option value="">Wszystkie lata</option>
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </FiltersWrapper>
      )}

      <ChartTitle>
        {selectedYear
          ? `Nowi vs. Stali Dostawcy w ${selectedYear}`
          : "Nowi vs. Stali Dostawcy (wszystkie lata)"}
      </ChartTitle>

      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={monthlyStats}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis allowDecimals={false} />
          <Tooltip />
          <Legend />
          <Bar dataKey="Nowi" fill="#82ca9d" />
          <Bar dataKey="Stali" fill="#9848c7" />
        </BarChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
};

export default NewVsRegularSuppliers;
